import { useEffect, useState, useCallback } from "react";

export function useOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  // Get token from localStorage
  const getToken = useCallback(() => localStorage.getItem("token"), []);

  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [getToken]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => !!getToken(), [getToken]);

  // For admin portal, assume users are vendors
  const isVendor = useCallback(() => isAuthenticated(), [isAuthenticated]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchOrders = async () => {
      if (!isAuthenticated()) {
        setError("Please sign in to view orders");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try vendor orders endpoint first (since this is admin portal)
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_PATH}/orders/vendor/allOrders`,
          {
            method: "GET",
            headers: getAuthHeaders(),
            signal: controller.signal,
          }
        );

        console.log("Orders API response status:", response.status);

        if (!response.ok) {
          throw new Error(`Failed to fetch orders (${response.status})`);
        }

        const data = await response.json();
        console.log("Fetched orders:", data);

        // Return raw orders data for components to transform
        const rawOrders = Array.isArray(data) ? data : [];
        console.log("Raw orders with userId:", rawOrders);

        setOrders(rawOrders);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Fetch orders error:", err);
          setError(err.message || "Failed to fetch orders");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
    return () => controller.abort();
  }, [searchTerm, page, getAuthHeaders, isAuthenticated]);

  // Update order status
  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/orders/vendorStatus/${status}/${orderId}`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update order status (${response.status})`);
      }

      const updatedOrder = await response.json();

      // Update local state immediately with the expected status
      setOrders((prevOrders) =>
        prevOrders.map((order) => {
          if (
            order.orderId === orderId ||
            order._id === orderId ||
            order.id === orderId
          ) {
            // Use the status we sent to the API as the authoritative source
            // since we know what the new status should be
            return {
              ...order,
              status: status, // Use the status parameter directly
            };
          }
          return order;
        })
      );

      return updatedOrder;
    } catch (error) {
      console.error("Update order status error:", error);
      throw error;
    }
  };

  // Cancel order
  const cancelOrder = async (orderId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/orders/cancel/${orderId}`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to cancel order (${response.status})`);
      }

      const updatedOrder = await response.json();

      // Update local state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === orderId || order._id === orderId
            ? { ...order, status: "cancelled" }
            : order
        )
      );

      return updatedOrder;
    } catch (error) {
      console.error("Cancel order error:", error);
      throw error;
    }
  };

  // Assign order to delivery agent with delivery date
  const assignOrderToAgent = async (
    orderId,
    deliveryAgentId,
    deliveryDate,
    timeSlot,
    notes
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/delivery-agents/assign-order`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            orderId,
            deliveryAgentId,
            expectedDeliveryDate: deliveryDate,
            deliveryTimeSlot: timeSlot,
            notes: notes || "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to assign order to agent (${response.status})`);
      }

      const result = await response.json();

      // Update local state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === orderId || order._id === orderId
            ? {
                ...order,
                status: "assigned",
                deliveryAgentId: result.order?.deliveryAgentId,
                expectedDeliveryDate: result.order?.expectedDeliveryDate,
                deliveryTimeSlot: result.order?.deliveryTimeSlot,
                assignedAt: result.order?.assignedAt,
              }
            : order
        )
      );

      return result;
    } catch (error) {
      console.error("Assign order to agent error:", error);
      throw error;
    }
  };

  // Update delivery status for orders
  const updateDeliveryStatus = async (orderId, status, deliveryAgentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/delivery-agents/orders/${orderId}/status`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            status,
            deliveryAgentId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to update delivery status (${response.status})`
        );
      }

      const result = await response.json();

      // Update local state immediately
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.orderId === orderId || order._id === orderId
            ? { ...order, status: status }
            : order
        )
      );

      return result;
    } catch (error) {
      console.error("Update delivery status error:", error);
      throw error;
    }
  };

  // Get order statistics
  const getOrderStats = () => {
    return {
      total: orders.length,
      processing: orders.filter((order) => order.status === "processing")
        .length,
      accepted: orders.filter((order) => order.status === "accepted").length,
      rejected: orders.filter((order) => order.status === "rejected").length,
      cancelled: orders.filter((order) => order.status === "cancelled").length,
    };
  };

  // Refresh orders
  const refreshOrders = () => {
    setPage((p) => p); // Trigger re-fetch
  };

  // Clear error
  const clearError = () => setError(null);

  return {
    orders,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    updateOrderStatus,
    assignOrderToAgent,
    updateDeliveryStatus,
    cancelOrder,
    getOrderStats,
    refreshOrders,
    clearError,
    isVendor: isVendor(),
    isAuthenticated: isAuthenticated(),
  };
}
